import { useQueryClient } from "@tanstack/react-query";
import { useStoreScenarioMutation } from "api/TenantSlug/ConnectionCreator/hooks.ts";
import { CCEntityType } from "components/CC/types/entityConfig.ts";
import { CC_ENTITY_TO_SCENARIO_CATEGORY } from "components/CC/types/scenarioCategory.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useCallback, useRef, useState } from "react";

interface UseEntityScenarioProps {
    entityId: string;
    entityType: CCEntityType;
    /** Optional scenario_id from entity - when BE adds this field, it can be passed directly */
    scenarioId?: string | null;
    /** Called after a new scenario is created - use to link it to the entity via scenario_ids */
    onScenarioCreated?: (scenarioId: string) => Promise<void>;
}

interface UseEntityScenarioResult {
    /** The resolved scenario ID (null if no scenario exists yet) */
    scenarioId: string | null;
    /** Whether a scenario exists for this entity */
    hasScenario: boolean;
    /** Function to create scenario - call this before first save */
    createScenario: () => Promise<string>;
    /** Whether scenario creation is in progress */
    isCreating: boolean;
}

/**
 * Hook to find or create a scenario for an entity.
 *
 * Flow:
 * 1. If scenarioId is provided (from entity include), use it directly
 * 2. If not found, return null (don't auto-create)
 * 3. Call createScenario() when user saves to create scenario first
 *
 * Note: Scenarios are m:n with entities. The scenarioId must be provided
 * by the caller from the entity's included scenarios (e.g. integration.scenarios?.[0]?.id).
 *
 * INVARIANT: Consumers MUST mount this hook with key={entityId} to force remount on entity
 * change. Without this, createdScenarioId state will not reset when entityId changes,
 * causing the wrong scenario to be used.
 */
export const useEntityScenario = ({
    entityId,
    entityType,
    scenarioId: providedScenarioId,
    onScenarioCreated,
}: UseEntityScenarioProps): UseEntityScenarioResult => {
    const { t } = usePaldockTranslation("hooks.use-entity-scenario");
    const category = CC_ENTITY_TO_SCENARIO_CATEGORY[entityType];
    const [createdScenarioId, setCreatedScenarioId] = useState<string | null>(null);

    // Track in-flight scenario creation to prevent duplicate calls
    const createScenarioPromiseRef = useRef<Promise<string> | null>(null);

    const queryClient = useQueryClient();
    const storeScenarioMutation = useStoreScenarioMutation();

    // Create scenario function - returns promise with new scenario ID
    const createScenario = useCallback(async (): Promise<string> => {
        // If we already have a scenario, return it
        if (providedScenarioId) return providedScenarioId;
        if (createdScenarioId) return createdScenarioId;

        // If creation is already in flight, return the same promise (prevents duplicates)
        if (createScenarioPromiseRef.current) return createScenarioPromiseRef.current;

        // Create new scenario
        const promise = new Promise<string>((resolve, reject) => {
            storeScenarioMutation.mutate(
                {
                    title: t("scenario_title", { entityType }),
                    is_enabled: true,
                    category,
                },
                {
                    onSuccess: async (response) => {
                        const newId = response.scenario.id;
                        try {
                            if (onScenarioCreated) {
                                await onScenarioCreated(newId);
                            }
                            setCreatedScenarioId(newId);
                            queryClient.refetchQueries({ queryKey: ["getScenarios"] });
                            resolve(newId);
                        } catch (linkError) {
                            reject(linkError);
                        }
                    },
                    onError: (error) => {
                        reject(new Error(error.message || t("errors.create_failed")));
                    },
                }
            );
        });

        createScenarioPromiseRef.current = promise;
        promise.finally(() => {
            createScenarioPromiseRef.current = null;
        });

        return promise;
    }, [
        providedScenarioId,
        createdScenarioId,
        entityId,
        entityType,
        category,
        onScenarioCreated,
        storeScenarioMutation,
        queryClient,
        t,
    ]);

    const scenarioId = providedScenarioId || createdScenarioId || null;
    const hasScenario = !!scenarioId;

    return {
        scenarioId,
        hasScenario,
        createScenario,
        isCreating: storeScenarioMutation.isPending,
    };
};
